import cn from 'classnames';
import { useMemo, useState } from 'react';
import { MdExpandMore, MdFileDownload, MdFlashOn, MdOutlineFileDownload } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { downloadPNG, exportMultiSheetToExcel } from '@src/utils/download-utils';
import { toPrettyNumber } from '@src/utils/performance-utils';
import {
  CITATION_ATTRIBUTES_LIMIT,
  CITATION_CARD_PRODUCTS_LIMIT,
  CITATION_MISSING_ATTRIBUTES_LIMIT,
  TOP_FOLLOWUPS_LIMIT
} from '../../alexa-visibility-constants';
import {
  buildFollowUpExportRows,
  getFollowUpQuestions,
  getLatestExcerpt,
  getMissingAttributes,
  getProductPositionStats,
  getTargetAttributes,
  getTargetCitationSummary,
  QueryCitationStats
} from '../../alexa-visibility-citation-utils';
import { getCitationTypeShares } from '../../alexa-visibility-utils';
import {
  formatCountOfTotal,
  formatPercentOfTotal,
  ProductPositionRows,
  TargetCitationRows
} from './CitationSummaryCards';

// The class that colors each citation type chip (see the stylesheet).
const CITATION_TYPE_CLASSES: Record<string, string> = {
  'Named Brand': 'is-named',
  Attribute: 'is-attribute',
  Competitive: 'is-competitive',
  'No Citation': 'is-none'
};

interface StatBoxProps {
  label: string;
  value: string;
  tone: 'rank' | 'score' | 'voice' | 'position';
}

const StatBox = ({ label, value, tone }: StatBoxProps) => (
  <div className={cn('aars-cc-stat', `is-${tone}`)}>
    <div className='aars-cc-stat-label'>{label}</div>
    <div className='aars-cc-stat-value'>{value}</div>
  </div>
);

interface CitationQueryCardProps {
  stats: QueryCitationStats;
  targetBrand: string;
  // Used for the PNG's element id and file name.
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  // Colors a competitor's tag on a missing attribute (the same brand colors as every other tab).
  getBrandColor: (brand: string) => string;
}

export const CitationQueryCard = ({
  stats,
  targetBrand,
  index,
  isExpanded,
  onToggle,
  getBrandColor
}: CitationQueryCardProps) => {
  const { results, query, runs } = stats;
  const [isExporting, setIsExporting] = useState(false);
  const setError = useSetError();
  const cardId = `citation-card-${index}`;

  // Everything below the header is only needed once the card is open.
  const details = useMemo(() => {
    if (!isExpanded) return undefined;
    return {
      citationTypes: getCitationTypeShares(results),
      summary: getTargetCitationSummary(results),
      products: getProductPositionStats(results),
      attributes: getTargetAttributes(results),
      missing: getMissingAttributes(results),
      excerpt: getLatestExcerpt(results),
      followUps: getFollowUpQuestions(results)
    };
  }, [isExpanded, results]);

  const handleDownloadPng = () => {
    downloadPNG(`#${cardId}`, `AlexaVisibility_Citation_${query}`, 'citation-card', [], false);
  };

  const handleDownloadFollowUps = async () => {
    if (!details) return;
    setIsExporting(true);
    try {
      await exportMultiSheetToExcel(
        [{ name: 'Follow-up Questions', data: buildFollowUpExportRows(details.followUps) }],
        `AlexaVisibility_FollowUps_${query}`
      );
    } catch (e) {
      setError({
        title: "Couldn't export follow-up questions",
        message: `Failed to export follow-up questions to Excel: ${e.message}`,
        reportError: e
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div
      id={cardId}
      className={cn('aars-cc-query-card', {
        'is-open': isExpanded,
        'is-varying': stats.variesAcrossRuns
      })}
    >
      <div className='aars-cc-query-header'>
        <button
          type='button'
          className='aars-cc-query-toggle'
          onClick={onToggle}
          aria-expanded={isExpanded}
        >
          <MdExpandMore className='aars-cc-query-caret' />
          {stats.variesAcrossRuns && (
            <span className='aars-cc-varies'>
              <MdFlashOn /> varies across runs
            </span>
          )}
          <span className='aars-cc-query-title'>{query}</span>
          <span className='aars-cc-query-runs'>
            {runs} {runs === 1 ? 'run' : 'runs'}
          </span>
        </button>
        <div className='aars-cc-stats'>
          <StatBox
            label='Visibility rank'
            tone='rank'
            value={stats.typicalRank !== undefined ? `#${stats.typicalRank}` : '—'}
          />
          <StatBox
            label='Visibility score'
            tone='score'
            value={`${stats.visibilityScore.toFixed(0)}%`}
          />
          <StatBox
            label='Share of voice'
            tone='voice'
            value={`${stats.shareOfVoice.toFixed(1)}%`}
          />
          <StatBox
            label='Avg position'
            tone='position'
            value={stats.avgPosition !== undefined ? stats.avgPosition.toFixed(1) : '—'}
          />
          <Button
            size={Size.SM}
            appearance={Appearance.OUTLINED}
            className='aars-cc-png-button no-print'
            data-html2canvas-ignore='true'
            leadingElement={<MdFileDownload />}
            onClick={handleDownloadPng}
          >
            PNG
          </Button>
        </div>
      </div>

      {details && (
        <div className='aars-cc-query-body'>
          <div className='aars-cc-section-grid'>
            <div>
              <div className='aars-cc-section-title'>Citation types</div>
              <div className='aars-cc-type-rows'>
                {details.citationTypes.map(({ label, count }) => (
                  <div key={label} className='aars-cc-type-row'>
                    <span className={cn('aars-cc-type-chip', CITATION_TYPE_CLASSES[label])}>
                      {label}
                    </span>
                    <span className='aars-cc-summary-value'>
                      <strong>{formatCountOfTotal(count, details.summary.total)}</strong>{' '}
                      {formatPercentOfTotal(count, details.summary.total)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className='aars-cc-section-title'>{targetBrand} citation summary</div>
              <TargetCitationRows summary={details.summary} />
            </div>
            <div>
              <div className='aars-cc-section-title'>
                Brand citation summary <span className='aars-cc-card-note'>top 3 positions</span>
              </div>
              <ProductPositionRows
                products={details.products.slice(0, CITATION_CARD_PRODUCTS_LIMIT)}
                total={details.summary.total}
                targetBrand={targetBrand}
              />
            </div>
          </div>

          <div className='aars-cc-section-grid is-two'>
            <div>
              <div className='aars-cc-section-title'>{targetBrand} attributes cited (all runs)</div>
              <div className='aars-cc-chips'>
                {details.attributes
                  .slice(0, CITATION_ATTRIBUTES_LIMIT)
                  .map(({ attribute, count }) => (
                    <span key={attribute} className='aars-cc-chip is-target'>
                      {attribute} <span className='aars-cc-chip-count'>{count}</span>
                    </span>
                  ))}
                {details.attributes.length === 0 && (
                  <span className='aars-cc-empty'>No attributes cited</span>
                )}
              </div>
            </div>
            <div>
              <div className='aars-cc-section-title'>
                Missing attributes{' '}
                <span className='aars-cc-card-note'>
                  (cited for competitors, never for {targetBrand})
                </span>
              </div>
              <div className='aars-cc-chips'>
                {details.missing.slice(0, CITATION_MISSING_ATTRIBUTES_LIMIT).map(item => (
                  <span
                    key={item.attribute}
                    className='aars-cc-chip is-missing'
                    style={{ borderColor: getBrandColor(item.brand) }}
                  >
                    {item.attribute}{' '}
                    <span
                      className='aars-cc-chip-brand'
                      style={{ color: getBrandColor(item.brand) }}
                    >
                      {item.brand}
                    </span>
                  </span>
                ))}
                {details.missing.length === 0 && (
                  <span className='aars-cc-empty'>No missing attributes</span>
                )}
              </div>
            </div>
          </div>

          {details.excerpt && (
            <div>
              <div className='aars-cc-section-title'>Latest excerpt</div>
              <blockquote className='aars-cc-excerpt'>“{details.excerpt}”</blockquote>
            </div>
          )}

          {details.followUps.length > 0 && (
            <div>
              <div className='aars-cc-section-header'>
                <div className='aars-cc-section-title'>
                  Alexa follow-up questions ({toPrettyNumber(details.followUps.length)} unique) ·
                  showing top {Math.min(TOP_FOLLOWUPS_LIMIT, details.followUps.length)}
                </div>
                <Button
                  size={Size.SM}
                  appearance={Appearance.OUTLINED}
                  className='no-print'
                  data-html2canvas-ignore='true'
                  leadingElement={<MdOutlineFileDownload />}
                  onClick={handleDownloadFollowUps}
                  loading={isExporting}
                >
                  Download all ({toPrettyNumber(details.followUps.length)})
                </Button>
              </div>
              <div className='aars-cc-chips'>
                {details.followUps.slice(0, TOP_FOLLOWUPS_LIMIT).map(({ label, count }) => (
                  <span key={label} className='aars-cc-chip'>
                    {label} <span className='aars-cc-chip-count'>×{count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
